import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingCart, ArrowRight, X as XIcon } from "lucide-react";
import {
  basketCount,
  subscribeBasket,
  clearBasket,
} from "@/lib/programBasket";

/**
 * Floating "Pending program" basket bar — pinned to the bottom of the
 * viewport when at least one service has been picked. Lets the user
 * jump straight to Program Builder or clear the basket from anywhere.
 *
 * Props:
 *   testIdPrefix: string — namespace for data-testid attributes (so multiple
 *     pages don't share selectors).
 *   hidden:       bool — render-suppress (e.g. when a drawer is open and
 *     would z-stack underneath).
 */
export default function BasketBar({ testIdPrefix = "basket", hidden = false }) {
  const [size, setSize] = useState(basketCount());
  useEffect(() => subscribeBasket((items) => setSize(items.length)), []);

  if (size === 0 || hidden) return null;

  return (
    <div
      data-testid={`${testIdPrefix}-bar`}
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 lg:left-[calc(16rem+50%-8rem)] lg:translate-x-0"
    >
      <div className="flex items-center gap-3 bg-slate-900 text-white rounded-full pl-3 pr-1.5 py-1.5 shadow-2xl border border-slate-800">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold">
          <ShoppingCart className="w-3.5 h-3.5 text-blue-300" />
          <span className="inline-flex items-center justify-center bg-blue-500 rounded-full text-[10px] min-w-[1.25rem] h-5 px-1.5 font-bold">
            {size}
          </span>
          service{size === 1 ? "" : "s"} pending
        </span>
        <button
          type="button"
          onClick={() => {
            clearBasket();
            toast.message("Pending program cleared.");
          }}
          data-testid={`${testIdPrefix}-clear`}
          className="p-1 rounded-full hover:bg-slate-800"
          title="Clear pending program"
        >
          <XIcon className="w-3.5 h-3.5 text-slate-400" />
        </button>
        <Link
          to="/portal/programs?from=basket"
          data-testid={`${testIdPrefix}-continue`}
          className="ml-1 inline-flex items-center gap-1.5 bg-white text-slate-900 rounded-full px-3.5 py-1.5 text-xs font-bold hover:bg-blue-50"
        >
          Continue to Program Builder
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}
